import { useDispatch } from 'react-redux'
import { RootState, TypedDispatch } from 'redux/store'
import React, { useEffect } from 'react'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-ultimate'
import Loading from 'modules/ui/atoms/Loading'
import AlertBanner from 'modules/ui/atoms/AlertBanner'
import useInitializeAnalytics from 'modules/analytics/useInitializeAnalytics'
import useDesignationURLInitialization from 'modules/order/state/designation/use-url-designation-initialization'
import Flow from 'modules/flow/components/flow/flow'
import { initializeApp } from '../../redux/actions/initializeApp'
import useConfig from 'modules/config/state/use-config'
import useInitializePaymentMethods from './hooks/useInitializePaymentsMethods'
import useAppVisibleChecker from '../../sdk/hooks/use-app-visible-checker'
import useSDKSafeguardTokenChecker from '../../sdk/hooks/use-sdk-safeguard-token-checker'
import SDKSafeguardError from '../../sdk/components/SDKSafeguardError'
import useUserAndSessionIdUpdater from '../../sdk/hooks/use-user-and-session-id-updater'
import { StatusType } from 'modules/status/state/status-types'
import useStatus from 'modules/status/state/use-status'
import { useFeatureFlag } from 'modules/optimizely'
import { OptimizelyFeatureFlags } from 'models/Optimizely'
import { InternalAnalyticsProvider } from 'utils/internalAnalytics'

const App = ({ env }: { env?: string }) => {
  const status = useStatus()
  const config = useConfig()
  const isEmbeddedBySDK = useSDKSafeguardTokenChecker({ env })

  const isMParticleEnabled = useFeatureFlag(OptimizelyFeatureFlags.classy_mparticle).enabled

  useInitializePaymentMethods()
  useDesignationURLInitialization()

  /**
   * Set up listeners for the User ID and User Session ID from the
   * SDK.
   */
  const userIdReady = useUserAndSessionIdUpdater()
  useInitializeAnalytics(userIdReady)

  /**
   * Set listener for when application is visible to the user
   */
  useAppVisibleChecker(userIdReady, !config.isEmbedded)

  const dispatch = useDispatch<TypedDispatch<RootState>>()

  // Dispatch action once component mounts so middlewares and other listeners can perform tasks.
  useEffect(() => {
    dispatch(initializeApp())
  }, [dispatch])

  if (!isEmbeddedBySDK && config?.isEmbedded) return <SDKSafeguardError />

  return (
    <GoogleReCaptchaProvider
      type="v3"
      isEnterprise
      explicit={{ badge: 'hidden' }}
      siteKey={process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY ?? ' '}
    >
      <InternalAnalyticsProvider
        appEnv={process.env.APP_ENV as string}
        mParticleApiKey={process.env.NEXT_PUBLIC_MPARTICLE_API_KEY as string}
        isMParticleEnabled={isMParticleEnabled}
      >
        {
          /*
        NOTE: Display the loading screen in the following scenarios:
        1. When the order is being processed
        2. When the order processing has succeeded

        The loading screen remains visible until the redirection to the TY page takes place.
        */
          (status.type === StatusType.PROCESSING || status.type === StatusType.SUCCEEDED) &&
            config.isEmbedded && <Loading />
        }

        {status.type === StatusType.FAILED && !!status.error && (
          <AlertBanner onClose={() => status.set(StatusType.IDLE)}>{status.error}</AlertBanner>
        )}

        <Flow />
      </InternalAnalyticsProvider>
    </GoogleReCaptchaProvider>
  )
}

export default React.memo(App) // Memoize App to prevent unnecessary re-renders
